/**
 * Contact form with Cloudflare Turnstile CAPTCHA protection
 */
import { createFileRoute } from "@tanstack/react-router"
import { useState, useEffect, useRef } from "react"
import { Container, Title, Text, Paper, Stack, TextInput, Textarea, Button, Alert, Group, Box } from "@mantine/core"
import { useForm } from "@mantine/form"
import { IconMail, IconAlertCircle, IconCheck, IconSend } from "@tabler/icons-react"
import { submitContactForm } from "../services/contact.service"
import type { ContactFormRequest } from "../types/api"

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: TurnstileOptions) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
    onTurnstileLoad?: () => void
  }
}

interface TurnstileOptions {
  sitekey: string
  callback: (token: string) => void
  "error-callback"?: () => void
  theme?: "light" | "dark" | "auto"
  size?: "normal" | "compact"
}

export const Route = createFileRoute("/contact")({
  component: ContactComponent,
})

type FormState = "idle" | "submitting" | "success" | "error"

function ContactComponent() {
  const [formState, setFormState] = useState<FormState>("idle")
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [turnstileToken, setTurnstileToken] = useState<string>("")
  const [turnstileLoaded, setTurnstileLoaded] = useState(false)
  const turnstileRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string>("")

  const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY

  const form = useForm<ContactFormRequest>({
    mode: "uncontrolled",
    initialValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
    },
    validate: {
      name: (value) => (value.length < 2 ? "Name must be at least 2 characters" : null),
      email: (value) => (/^\S+@\S+\.\S+$/.test(value) ? null : "Invalid email address"),
      subject: (value) => (value.length < 3 ? "Subject must be at least 3 characters" : null),
      message: (value) => (value.length < 10 ? "Message must be at least 10 characters" : null),
    },
  })

  useEffect(() => {
    const existingScript = document.querySelector('script[src*="challenges.cloudflare.com"]')
    if (existingScript) {
      // Wait for window.turnstile to be available
      const checkTurnstile = setInterval(() => {
        if (window.turnstile) {
          setTurnstileLoaded(true)
          clearInterval(checkTurnstile)
        }
      }, 100)
      return () => clearInterval(checkTurnstile)
    }

    const script = document.createElement("script")
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
    script.async = true
    script.defer = true

    const handleScriptLoad = () => {
      // Wait for window.turnstile to be available after load
      const checkTurnstile = setInterval(() => {
        if (window.turnstile) {
          setTurnstileLoaded(true)
          clearInterval(checkTurnstile)
        }
      }, 100)

      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkTurnstile)
      }, 5000)
    }

    script.addEventListener("load", handleScriptLoad)
    document.head.appendChild(script)

    return () => {
      script.removeEventListener("load", handleScriptLoad)
    }
  }, [])

  useEffect(() => {
    if (!turnstileLoaded || !turnstileRef.current || !window.turnstile || !TURNSTILE_SITE_KEY) {
      return
    }

    if (widgetIdRef.current) {
      return
    }

    try {
      const widgetId = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => {
          setTurnstileToken(token)
        },
        "error-callback": () => {
          setTurnstileToken("")
          setErrorMessage("CAPTCHA verification failed. Please try again.")
        },
        theme: "auto",
        size: "normal",
      })
      widgetIdRef.current = widgetId
    } catch (error) {
      console.error("Error rendering Turnstile widget:", error)
    }
  }, [turnstileLoaded, TURNSTILE_SITE_KEY])

  async function handleSubmit(values: ContactFormRequest): Promise<void> {
    if (!turnstileToken) {
      setFormState("error")
      setErrorMessage("Please complete the CAPTCHA verification.")
      return
    }

    setFormState("submitting")
    setErrorMessage("")

    const result = await submitContactForm({ ...values, turnstileToken })

    result.match({
      Left: (error) => {
        setFormState("error")
        setErrorMessage(error.message)
        if (window.turnstile && widgetIdRef.current) {
          window.turnstile.reset(widgetIdRef.current)
          setTurnstileToken("")
        }
      },
      Right: () => {
        setFormState("success")
        form.reset()
        if (window.turnstile && widgetIdRef.current) {
          window.turnstile.reset(widgetIdRef.current)
          setTurnstileToken("")
        }
      },
    })
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Box ta="center" mb="xl">
          <Box
            style={{
              background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
              borderRadius: "16px",
              padding: "12px",
              boxShadow: "0 8px 24px rgba(59, 130, 246, 0.3)",
              display: "inline-flex",
              marginBottom: "24px",
            }}
          >
            <IconMail size={32} color="white" />
          </Box>
          <Title order={1} size="h1" mb="md">
            Contact Us
          </Title>
          <Text size="lg" c="dimmed" maw={600} mx="auto">
            Have questions, suggestions, or information to share? We'd love to hear from you.
          </Text>
        </Box>

        {/* Success Alert */}
        {formState === "success" && (
          <Alert icon={<IconCheck size={20} />} title="Message Sent Successfully!" color="green" radius="md">
            Thank you for contacting us. We've received your message and will respond as soon as possible.
          </Alert>
        )}

        {/* Error Alert */}
        {formState === "error" && (
          <Alert icon={<IconAlertCircle size={20} />} title="Error Sending Message" color="red" radius="md">
            {errorMessage || "Something went wrong. Please try again later."}
          </Alert>
        )}

        {/* Contact Form */}
        <Paper shadow="sm" radius="md" p="xl">
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <TextInput
                label="Name"
                placeholder="Your name"
                required
                {...form.getInputProps("name")}
                key={form.key("name")}
              />

              <TextInput
                label="Email"
                placeholder="your.email@example.com"
                type="email"
                required
                {...form.getInputProps("email")}
                key={form.key("email")}
              />

              <TextInput
                label="Subject"
                placeholder="What is this about?"
                required
                {...form.getInputProps("subject")}
                key={form.key("subject")}
              />

              <Textarea
                label="Message"
                placeholder="Your message..."
                required
                minRows={6}
                maxRows={12}
                {...form.getInputProps("message")}
                key={form.key("message")}
              />

              {TURNSTILE_SITE_KEY && <Box ref={turnstileRef} mt="md" />}

              <Group justify="flex-end" mt="md">
                <Button
                  type="submit"
                  size="md"
                  leftSection={<IconSend size={18} />}
                  loading={formState === "submitting"}
                  disabled={formState === "submitting"}
                  style={{
                    background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                  }}
                >
                  Send Message
                </Button>
              </Group>
            </Stack>
          </form>
        </Paper>

        {/* Additional Info */}
        <Paper shadow="sm" radius="md" p="lg">
          <Stack gap="md">
            <Title order={3} size="h4">
              Contact Information
            </Title>
            <Text size="sm" c="dimmed" lh={1.6}>
              We welcome inquiries from journalists, researchers, legal professionals, and concerned citizens. Please
              allow 2-3 business days for a response.
            </Text>
          </Stack>
        </Paper>

        {/* Legal Notice */}
        <Paper shadow="sm" radius="md" p="lg">
          <Stack gap="md">
            <Title order={3} size="h4">
              Privacy & Legal Notice
            </Title>
            <Text size="sm" c="dimmed" lh={1.6}>
              Your contact information will be used solely to respond to your inquiry. We do not share, sell, or
              distribute contact information to third parties. By submitting this form, you acknowledge that you have
              read our{" "}
              <Text component="a" href="/privacy" c="blue" td="none">
                Privacy Policy
              </Text>
              .
            </Text>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  )
}
